import { useSnackbar } from "notistack";
import { FC, useEffect, useState } from "react";

import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Button, FormControl, IconButton, MenuItem, Select, Stack, TextField } from "@mui/material";

import { api } from "../../../../../utils";
import { IError } from "../../../../../models/ReportPageModels";
import { BranchModel } from "../../../../../models/AdminPageModels";

interface AdminIccBranchesResponse {
    data: IccBranchesData;
    error: IError;
}
interface IccBranchesData {
    branches: BranchModel[];
}

interface CreateIccBranchRequest {
    branchName: string;
    database: string;
    logoUrl: string;
}

interface CreateIccBranchResponse {
    data: CreateIccBranchData;
    error: IError;
}
interface CreateIccBranchData {
    status: string;
}

interface GetDatabases {
    data: GetDatabasesData;
    error: IError;
}
interface GetDatabasesData {
    databases: string[];
}

interface CreateIccBranchProps {
    workflowNextStep?: (branchName: string) => void;
    createdDatabase?: string | null;
}

const CreateIccBranch: FC<CreateIccBranchProps> = ({ workflowNextStep = null, createdDatabase = null }) => {
    const { enqueueSnackbar } = useSnackbar();

    const [branchName, setBranchName] = useState<string>("");
    const [selectedDatabase, setSelectedDatabase] = useState<string>("");
    const [logoUrl, setLogoUrl] = useState<string>("");

    const [branches, setBranches] = useState<string[]>([]);
    const [databases, setDatabases] = useState<string[]>([]);

    const [isLoading, setIsLoading] = useState<boolean>(true);

    useEffect(() => {
        GetBranches();
        GetDatabases();
    }, []);

    const GetBranches = async () => {
        try {
            const response: AdminIccBranchesResponse = await api.get("api/Admin/GetICCBranches", {
                headers: {
                    "Authorization": localStorage.getItem("token")!
                }
            }).json();

            if (response.error == null) {
                setBranches(response.data.branches.map(x => x.branchName));

                setIsLoading(false);
            }
            else {
                enqueueSnackbar(response.error.description, { variant: "error" });
            }
        }
        catch (err) {
            enqueueSnackbar((err as Error).message, { variant: "error" });
        }
    };

    const GetDatabases = async () => {
        try {
            const response: GetDatabases = await api.get("api/Admin/GetDatabases", {
                headers: {
                    "Authorization": localStorage.getItem("token")!
                }
            }
            ).json();

            if (response.error == null) {
                setDatabases(response.data.databases);

                // database created in workflow previous step
                if (createdDatabase) {
                    setSelectedDatabase(createdDatabase);
                }
            }
            else {
                enqueueSnackbar(response.error.description, { variant: "error" });
            }
        }
        catch (err) {
            enqueueSnackbar((err as Error).message, { variant: "error" });
        }
    };

    const CreateBranch = async () => {
        if (branchName === "" || selectedDatabase === "") {
            enqueueSnackbar("Please recheck the field", { variant: "error" });
            return;
        }

        var body: CreateIccBranchRequest = {
            branchName: branchName,
            database: selectedDatabase,
            logoUrl: logoUrl
        }

        try {
            const response: CreateIccBranchResponse = await api.post("api/Admin/CreateIccBranch", {
                json: body,
                headers: {
                    "Authorization": localStorage.getItem("token")!
                }
            }).json();

            if (response.error == null) {
                enqueueSnackbar("Created!", { variant: "success" })

                // for workflow function to step passed
                if (workflowNextStep)
                    workflowNextStep(branchName);

                // clear fields
                setBranchName("");
                setSelectedDatabase("");
                setLogoUrl("");
            }
            else {
                enqueueSnackbar(response.error.description, { variant: "error" });
            }
        }
        catch (err) {
            enqueueSnackbar((err as Error).message, { variant: "error" });
        }
    }

    const CopyDatabaseToClipboard = async () => {
        if (selectedDatabase === "") return;
        enqueueSnackbar("Copied!", { variant: "success" });
        await navigator.clipboard.writeText(selectedDatabase);
    }

    const IsBranchValid = !branches.includes(branchName);
    const IsFormValid = branchName !== "" && selectedDatabase !== "" && IsBranchValid;

    return (
        <Stack>
            {/* BranchName */}
            <Stack
                direction={"row"}
                alignItems={"center"} >
                <p style={{ width: '110px' }}>Branch name: </p>
                <TextField
                    value={branchName}
                    onChange={(newValue: any) => { setBranchName(newValue.target.value) }}
                    sx={{ minWidth: 245 }}
                    size="small"
                    error={!IsBranchValid}
                    helperText={!IsBranchValid ? "Branch is already in use" : ""}
                    disabled={isLoading} />
            </Stack>
            {/* Database */}
            <Stack
                direction={"row"}
                alignItems={"center"}>
                <p style={{ width: '110px' }}>Database: </p>
                <FormControl sx={{ minWidth: 245 }}>
                    <Select
                        value={selectedDatabase}
                        onChange={(newValue: any) => { setSelectedDatabase(newValue.target.value); }}
                        size="small"
                        defaultValue={""}>
                        <MenuItem
                            value={""}>
                            {"Select database"}
                        </MenuItem>
                        {databases.map((database: string, index) =>
                            <MenuItem
                                key={index}
                                value={database}>
                                {database}
                            </MenuItem>
                        )}
                    </Select>
                </FormControl>
                <IconButton onClick={CopyDatabaseToClipboard}>
                    <ContentCopyIcon />
                </IconButton>
            </Stack>
            {/* Logo */}
            <Stack
                direction={"row"}
                alignItems={"center"} >
                <p style={{ width: '110px' }}>Logo url: </p>
                <TextField
                    value={logoUrl}
                    onChange={(newValue: any) => { setLogoUrl(newValue.target.value) }}
                    sx={{ minWidth: 400 }}
                    size="small" />
            </Stack>

            <Button
                variant="outlined"
                onClick={() => CreateBranch()}
                sx={{ width: '82px' }}
                disabled={!IsFormValid}>
                Create
            </Button>
        </Stack>
    )
}

export default CreateIccBranch;